import { JsonType } from 'posthog-js';
import { createContext, useContext } from 'react';
import { z } from 'zod';

import { Logger } from '@eluve/logger';
import {
  FeatureFlagDefinitions,
  FeatureFlagKeys,
  featureFlagsWithPayloads,
} from '@eluve/posthog';

import { useAnalytics } from './useAnalytics';

export type FeatureFlagContextType = {
  flagDefinitions: FeatureFlagDefinitions;
  getFeatureFlagData: (flagKey: FeatureFlagKeys) => JsonType;
  logger: Logger;
};

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

export const FeatureFlagProvider = FeatureFlagContext.Provider;

const useFeatureFlagContext = () => {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) {
    throw new Error(
      'useFeatureFlagContext must be used within a FeatureFlagProvider',
    );
  }

  return ctx;
};

export const useIsPostHogFeatureFlagEnabled = (flagKey: FeatureFlagKeys) => {
  const analytics = useAnalytics();
  return analytics.isFeatureEnabled(flagKey) ?? false;
};

export const useIsV2AudioStackEnabled = () => {
  return useIsPostHogFeatureFlagEnabled('AUDIO_STACK_V2');
};

export const useFeatureFlagPayload = <T extends FeatureFlagKeys>(
  flagKey: T,
): z.infer<(typeof featureFlagsWithPayloads)[T]['schema']> => {
  const { getFeatureFlagData, flagDefinitions } = useFeatureFlagContext();

  const flagDefinition = flagDefinitions[flagKey];
  if (!flagDefinition) {
    throw new Error(`Missing flag definition for ${flagKey}`);
  }

  const { defaultValue, schema } = flagDefinition;

  const flagData = getFeatureFlagData(flagKey);
  const { success, data: parsed } = schema.safeParse(flagData);

  if (success) {
    return parsed as z.infer<(typeof featureFlagsWithPayloads)[T]['schema']>;
  }

  // TODO(jesse)[ELU-2301] - Re-enable this warning once we have PostHog proxy to
  // prevent adblocking breaking things

  // logger.warn(
  //   'Got feature flag data that did not match schema. Returning fallback',
  //   {
  //     flag: flagKey,
  //     data: flagData,
  //   },
  // );

  return defaultValue as z.infer<
    (typeof featureFlagsWithPayloads)[T]['schema']
  >;
};
